import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Button from 'components/Button';
import Form from 'components/Form';
import { useForm } from 'react-hook-form';
import TextField from 'components/TextField';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useApiDelete, useApiGet, useApiPost, useApiPut } from 'hooks/useApi';
import useFieldSetErrors from 'hooks/useFieldSetErrors';
import { useToast } from 'hooks/useToast';
import { Loader } from 'react-bootstrap-typeahead';
import Modal from 'components/Modal';
import ToastType from 'enum/ToastType';
import SelectField from 'components/SelectField';
import { AdminExternalNewsCategory } from 'models/AdminExternalNewsCategory';
import { AdminExternalNewsChannel } from 'models/AdminExternalNewsChannel';
import Pagination from 'models/Pagination';

const schema = yup
  .object({
    name: yup.string().required('O nome é obrigatório.'),
    channel: yup.string().required('O canal é obrigatório.'),
    urlPath: yup.string().required('O caminho/URL é obrigatório.'),
    isActive: yup.string().oneOf(['true', 'false']).required(),
    rootField: yup.string().default(''),
    itemField: yup.string().default(''),
    titleField: yup.string().default(''),
    descriptionField: yup.string().default(''),
    linkField: yup.string().default(''),
    imageField: yup.string().default(''),
    useWebContent: yup.string().oneOf(['true', 'false']).required()
  })
  .required();

const AdminNewsCategoriesFormPage = () => {
  const params = useParams();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const navigate = useNavigate();
  const { addToast } = useToast();

  const categoryId = params.id;

  const { data, isLoading: isFetching } = useApiGet<AdminExternalNewsCategory>(
    `/admin/news/categories`,
    { id: categoryId, enabled: !!categoryId }
  );

  const { data: channels } = useApiGet<Pagination<AdminExternalNewsChannel>>(
    '/admin/news/channels',
    {
      params: { pageSize: 100, page: 1 }
    }
  );

  const channelOptions = useMemo(
    () => (channels?.items || []).map((c) => ({ value: c.id, label: c.name })),
    [channels?.items]
  );

  const { mutate: mutateCreate, isPending: isCreating } = useApiPost<
    AdminExternalNewsCategory
  >('/admin/news/categories');
  const { mutate: mutateUpdate, isPending: isUpdating } = useApiPut<
    AdminExternalNewsCategory
  >('/admin/news/categories', categoryId);
  const { mutate: mutateDelete, isPending: isDeleting } = useApiDelete(
    '/admin/news/categories',
    categoryId
  );

  const isSaving = isCreating || isUpdating;
  const mutate = categoryId ? mutateUpdate : mutateCreate;

  const formMethods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      isActive: 'true',
      useWebContent: 'false'
    }
  });

  const onError = useFieldSetErrors(formMethods.setError);

  useEffect(() => {
    if (!data) return;
    formMethods.reset({
      name: data.name,
      channel: data.channel?.id,
      urlPath: data.urlPath,
      isActive: String(data.isActive),
      rootField: data.rootField || '',
      itemField: data.itemField || '',
      titleField: data.titleField || '',
      descriptionField: data.descriptionField || '',
      linkField: data.linkField || '',
      imageField: data.imageField || '',
      useWebContent: String(Boolean(data.useWebContent))
    } as any);
  }, [data, formMethods]);

  const yesNoOptions = useMemo(
    () => [
      { value: 'false', label: 'Não' },
      { value: 'true', label: 'Sim' }
    ],
    []
  );

  const isActiveOptions = useMemo(
    () => [
      { value: 'true', label: 'Ativo' },
      { value: 'false', label: 'Inativo' }
    ],
    []
  );

  const handleOnSuccess = useCallback(() => {
    addToast(ToastType.SUCCESS, 'Categoria salva com sucesso');
    navigate('/admin/news/categories');
  }, [addToast, navigate]);

  const handleOnSubmit = useCallback(
    (payload: any) => {
      mutate(payload as any, {
        onError,
        onSuccess: handleOnSuccess
      });
    },
    [mutate, onError, handleOnSuccess]
  );

  const handleOnDelete = useCallback(() => {
    if (!categoryId) return;
    mutateDelete(null as any, {
      onError: (response: any) => {
        addToast(
          ToastType.ERROR,
          response.errors?.detail?.toString() || 'Erro ao excluir categoria'
        );
      },
      onSuccess: () => {
        addToast(ToastType.SUCCESS, 'Categoria excluída com sucesso');
        navigate('/admin/news/categories');
      }
    });
  }, [addToast, categoryId, mutateDelete, navigate]);

  return (
    <>
      <div className="padding-page">
        <Form onSubmit={handleOnSubmit} {...formMethods}>
          <Row className="d-flex flex-column flex-md-row">
            <Col className="col-12 col-md">
              <div>
                <p className="text-gray-600">
                  <Link
                    to="/admin/news/categories"
                    className="text-decoration-none text-gray-600"
                  >
                    Categorias de Notícias
                  </Link>
                  {' / '}
                  {categoryId ? 'Editar' : 'Nova'}
                </p>
                <h4 className="fw-bold">
                  {categoryId ? 'Editar Categoria' : 'Nova Categoria'}
                </h4>
                <span className="small-typography text-gray-600">
                  Configure as informações da categoria de notícias.
                </span>
              </div>
            </Col>
            <Col className="col-12 col-md align-text mt-3 mt-md-0 d-flex gap-2 justify-content-end">
              {categoryId && (
                <Button
                  type="button"
                  color="danger"
                  loading={isDeleting}
                  icon="trash"
                  onClick={() => setShowDeleteConfirmation(true)}
                >
                  Excluir
                </Button>
              )}
              <Button
                color="primary"
                icon="floppy"
                loading={isSaving}
                disabled={!formMethods.formState.isValid}
              >
                Salvar
              </Button>
            </Col>
          </Row>

          {isFetching ? (
            <div className="text-center p-5">
              <Loader />
            </div>
          ) : (
            <div className="bg-white rounded shadow p-4 mt-4">
              <Row>
                <Col md="6">
                  <TextField
                    name="name"
                    label="Nome:"
                    placeholder="Nome da categoria"
                  />
                </Col>
                <Col md="6">
                  <SelectField
                    name="channel"
                    label="Canal:"
                    options={channelOptions}
                  />
                </Col>
              </Row>
              <Row>
                <Col md="6">
                  <TextField
                    name="urlPath"
                    label="URL / Caminho:"
                    placeholder="/rss/..."
                  />
                </Col>
                <Col md="3">
                  <SelectField
                    name="isActive"
                    label="Status:"
                    options={isActiveOptions}
                  />
                </Col>
                <Col md="3">
                  <SelectField
                    name="useWebContent"
                    label="Usar conteúdo do site:"
                    options={yesNoOptions}
                  />
                </Col>
              </Row>

              <hr className="my-4" />
              <h6 className="fw-bold mb-3">Configuração de Campos RSS</h6>
              <p className="small text-muted mb-3">
                Configure os campos para extrair as informações do RSS.
              </p>

              <Row>
                <Col md="6">
                  <TextField
                    name="rootField"
                    label="Campo Raiz (rootField):"
                    placeholder="Ex.: rss"
                  />
                </Col>
                <Col md="6">
                  <TextField
                    name="itemField"
                    label="Campo de Item (itemField):"
                    placeholder="Ex.: channel.item"
                  />
                </Col>
              </Row>
              <Row>
                <Col md="6">
                  <TextField
                    name="titleField"
                    label="Campo de Título (titleField):"
                    placeholder="Ex.: title"
                  />
                </Col>
                <Col md="6">
                  <TextField
                    name="descriptionField"
                    label="Campo de Descrição (descriptionField):"
                    placeholder="Ex.: description"
                  />
                </Col>
              </Row>
              <Row>
                <Col md="6">
                  <TextField
                    name="linkField"
                    label="Campo de Link (linkField):"
                    placeholder="Ex.: link"
                  />
                </Col>
                <Col md="6">
                  <TextField
                    name="imageField"
                    label="Campo de Imagem (imageField):"
                    placeholder="Ex.: enclosure.url"
                  />
                </Col>
              </Row>
            </div>
          )}
        </Form>
      </div>

      <Modal
        primaryButtonAction={handleOnDelete}
        primaryButtonLabel="Excluir"
        title="Atenção!"
        isOpen={showDeleteConfirmation}
        showCloseButton={false}
        toggle={() => setShowDeleteConfirmation((x) => !x)}
        secondaryButtonAction={() => setShowDeleteConfirmation(false)}
        secondaryButtonLabel="Cancelar"
        isLoading={isDeleting}
      >
        <p>
          Deseja realmente excluir a categoria? <br />
          <small>Essa alteração não poderá ser revertida.</small>
        </p>
      </Modal>
    </>
  );
};

export default AdminNewsCategoriesFormPage;
